import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store/useStore';
import { getCountry } from '@/utilities/getters/accountGetters';
import { COUNTRY_ABBREVIATION } from '@/utilities/constants/account';
import {
  getExperimentValue,
  NET_POSITION_ENABLED_EXPERIMENT,
  NET_POSITION_FORCE_EXPERIMENT,
  NET_POSITION_GAME_SWITCH_NOT_AVAILABLE_EXPERIMENT,
} from '@/utilities/logger/amplitude/experiments/getExperimentValue';
import { useExperimentExposure } from '@/utilities/logger/amplitude/experiments/experimentExposure';

const isExperimentVariant = (experimentValue?: string) =>
  experimentValue === 'treatment';

/**
 * Canada net-position feature gate:
 *   country === CA && net_position_enabled === 'treatment' && provider
 *   `show_net_position` && (superclass-slots OR (IGT/Playtech &&
 *   net_position_game_switch_not_available === 'treatment')). `net_position_force`
 *   overrides everything.
 */
export const useEnableNetPositionFeature = () => {
  const { provider, superclass, showNetPosition } = useStore(
    useShallow((s) => ({
      provider: s.gameContext.provider,
      superclass: s.gameContext.superclass,
      showNetPosition: s.gameContext.showNetPosition,
    })),
  );
  const amplitudeExperiments = useStore((s) => s.amplitudeExperiments);
  useExperimentExposure(
    NET_POSITION_ENABLED_EXPERIMENT,
    NET_POSITION_FORCE_EXPERIMENT,
    NET_POSITION_GAME_SWITCH_NOT_AVAILABLE_EXPERIMENT,
  );

  const netPositionEnabled = getExperimentValue(
    amplitudeExperiments,
    NET_POSITION_ENABLED_EXPERIMENT,
  );
  const netPositionForce = getExperimentValue(
    amplitudeExperiments,
    NET_POSITION_FORCE_EXPERIMENT,
  );
  const netPositionGameSwitchNotAvailable = getExperimentValue(
    amplitudeExperiments,
    NET_POSITION_GAME_SWITCH_NOT_AVAILABLE_EXPERIMENT,
  );

  const isCanada =
    getCountry(process.env.COUNTRY ?? '') === COUNTRY_ABBREVIATION.CANADA;

  const netPositionFeatureEnabled =
    isCanada &&
    isExperimentVariant(netPositionEnabled) &&
    !!showNetPosition &&
    (superclass === 'superclass-slots' ||
      ((provider === 'IGT' || provider === 'PLAYTECH') &&
        isExperimentVariant(netPositionGameSwitchNotAvailable)));

  return {
    netPositionFeatureEnabled:
      netPositionFeatureEnabled || isExperimentVariant(netPositionForce),
    providerNetPositionEnabled: !!showNetPosition,
  };
};
